import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/all";
gsap.registerPlugin(ScrollTrigger);
import { useEffect, useRef, useState } from "react";

import { hightlightsSlides } from "../constants";
import { pauseImg, playImg, replayImg } from "../utils";

const VideoCarousel = () => {
  const videoRef = useRef([]);
  const videoSpanRef = useRef([]);
  const videoDivRef = useRef([]);

  // 视频播放状态
  const [video, setVideo] = useState({
    isEnd: false,
    startPlay: false,
    videoId: 0,
    isLastVideo: false,
    isPlaying: false,
  });

  const [loadedData, setLoadedData] = useState([]);
  const { isLastVideo, startPlay, videoId, isPlaying } = video;

  /* ---------- GSAP 滑屏动画 ---------- */
  useGSAP(() => {
    gsap.to("#slider", {
      transform: `translateX(${-100 * videoId}%)`,
      duration: 2,
      ease: "power2.inOut",
    });
  }, [videoId]);

  /* ---------- 视频 / 进度条动画 ---------- */
  useEffect(() => {
    if (!startPlay) return;

    const span = videoSpanRef.current[videoId];
    const progressBar = videoDivRef.current[videoId];
    if (!span || !progressBar) return;

    let currentProgress = 0;

    const anim = gsap.to(span, {
      width: "100%",
      backgroundColor: "white",
      ease: "none",
      onUpdate: () => {
        const p = Math.ceil(anim.progress() * 100);
        if (p !== currentProgress) {
          currentProgress = p;
          gsap.to(progressBar, {
            width:
              window.innerWidth < 760
                ? "10vw"
                : window.innerWidth < 1200
                ? "10vw"
                : "4vw",
          });
        }
      },
      onComplete: () => {
        gsap.to(progressBar, { width: "12px" });
        gsap.to(span, { backgroundColor: "#afafaf" });
      },
    });

    const updateProgress = () => {
      const vid = videoRef.current[videoId];
      if (vid) {
        anim.progress(vid.currentTime / hightlightsSlides[videoId].videoDuration);
      }
    };

    if (isPlaying) {
      gsap.ticker.add(updateProgress);
    } else {
      gsap.ticker.remove(updateProgress);
    }

    return () => gsap.ticker.remove(updateProgress);
  }, [videoId, startPlay, isPlaying]);

  /* ---------- 播放控制 ---------- */
  useEffect(() => {
    if (loadedData.length === hightlightsSlides.length && !startPlay) {
      // 全部视频加载完 → 自动开始播放
      videoRef.current[0].play();
      setVideo({
        isEnd: false,
        startPlay: true,
        videoId: 0,
        isLastVideo: false,
        isPlaying: true,
      });
    }
  }, [loadedData, startPlay]);
  useEffect(() => {
    const vid = videoRef.current[videoId];
    if (!vid) return;

    // 只在切换视频时重置
    vid.currentTime = 0;
    if (isPlaying) vid.play();
  }, [videoId]);
  useEffect(() => {
    const vid = videoRef.current[videoId];
    if (!vid) return;

    if (isPlaying) {
      vid.play();
    } else {
      vid.pause();
    }
  }, [isPlaying, videoId]);

  /* ---------- 事件封装 ---------- */
  const handleProcess = (type, i) => {
    switch (type) {
      case "video-end":
      case "video-last": {
        const nextId = (i + 1) % hightlightsSlides.length;
        setVideo((prev) => ({
          ...prev,
          isEnd: false,
          videoId: nextId,
          isLastVideo: nextId === hightlightsSlides.length - 1,
          isPlaying: true,
        }));
        break;
      }
      case "video-reset":
        setVideo({
          isEnd: false,
          startPlay: true,
          videoId: 0,
          isLastVideo: false,
          isPlaying: true,
        });
        break;
      case "pause":
        setVideo((prev) => ({ ...prev, isPlaying: false }));
        break;
      case "play":
        setVideo((prev) => ({ ...prev, isPlaying: true }));
        break;
      default:
    }
  };

  const handleLoadedMetaData = (_, i) =>
    setLoadedData((prev) => [...prev, true]);

  return (
    <>
      {/* 视频列表 */}
      <div className="flex items-center" >
        {hightlightsSlides.map((list, i) => (
          <div key={list.id} id="slider" className="sm:pr-20 pr-10">
            <div className="video-carousel_container">
              <div className="w-full h-full flex-center rounded-3xl overflow-hidden bg-black">
                <video
                  id="video"
                  playsInline
                  muted
                  preload="auto"
                  className={`pointer-events-none`}
                  ref={(el) => (videoRef.current[i] = el)}
                  onLoadedMetadata={(e) => handleLoadedMetaData(e, i)}
                  onPlay={() => setVideo((prev) => ({ ...prev, isPlaying: true }))}
                  onEnded={() =>
                    i !== 3
                      ? handleProcess("video-end", i)
                      : handleProcess("video-last", i)
                  }
                  style={{objectFit: 'cover', objectPosition: 'center'}}
                >
                  <source src={list.video} type="video/mp4" />
                </video>
              </div>

              <div className="absolute top-12 left-[5%] z-10 text-white -translate-y-9 sm:translate-y-0">
                {list.textLists.map((text, idx) => (
                  <p key={idx} className="md:text-4xl text-lg font-medium">
                    {text}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 进度指示 & 暂停 / 重播按钮 */}
      <div className="relative flex-center mt-10">
        <div className="flex-center py-5 px-7 bg-black-gray backdrop-blur rounded-full">
          {hightlightsSlides.map((_, i) => (
            <span
              key={i}
              className="mx-2 w-3 h-3 bg-gray-200 rounded-full relative cursor-pointer"
              ref={(el) => (videoDivRef.current[i] = el)}
            >
              <span
                className="absolute top-0 left-0 h-full w-full rounded-full"
                ref={(el) => (videoSpanRef.current[i] = el)}
              />
            </span>
          ))}
        </div>

        <button className="control-btn bg-black-gray">
          <img
            src={isLastVideo ? replayImg : !isPlaying ? playImg : pauseImg}
            alt={isLastVideo ? "replay" : !isPlaying ? "play" : "pause"}
            onClick={() =>
              isLastVideo
                ? handleProcess("video-reset")
                : !isPlaying
                ? handleProcess("play")
                : handleProcess("pause")
            }
          />
        </button>
      </div>
    </>
  );
};

export default VideoCarousel;